import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Chip,
  Switch,
  Tabs,
  Tab,
  Box,
  Tooltip,
  CircularProgress,
  Typography
} from '@mui/material';
import { EditOutlined } from '@ant-design/icons';
import { BsEyeFill } from 'react-icons/bs';
import { useNavigate } from 'react-router';
import MainCard from 'components/MainCard';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import Lottie from 'react-lottie';
import lottieLoader from '../../assets/Lottie/NoLoaderData.json';
import { getUsersByRole } from 'services/users/allUsers';
import { getstudents } from 'services/student/getStudents';

const loaderOptions = {
  loop: true,
  autoplay: true,
  animationData: lottieLoader,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice'
  }
};
export default function StudentList() {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [students, setStudents] = useState([]);

  const { data, isLoading, isError } = useQuery({
    queryFn: () => getstudents(),
    queryKey: ['students']
  });

  const studentsData = data?.data;

  useEffect(() => {
    if (studentsData?.length) {
      const enriched = studentsData.map((student) => ({
        studentId: student.studentId,
        studentName: student.student_name,
        studentEmail: student.student_email || '',
        studentContact: student.student_phone || '',
        coach_name: student.coach_name || '',
        registrationDate: student.created_at,
        studentStatus: student.status !== undefined ? student.status : 1
      }));
      setStudents(enriched);
    }
  }, [studentsData]);

  const allCount = students.length;
  const activeCount = students.filter((student) => student.studentStatus === 1).length;
  const inactiveCount = students.filter((student) => student.studentStatus === 0).length;

  // Tab Change Handler
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Filter Students Based on Search & Status
  const filteredStudents = students
    .filter((student) => {
      if (tabValue === 1) return student.studentStatus === 1; // Active
      if (tabValue === 2) return student.studentStatus === 0; // Inactive
      return true;
    })
    .filter((student) => student.studentName.toLowerCase().includes(searchTerm.toLowerCase()));

  // Toggle Active/Inactive Status (local state only)
  const handleToggleStatus = (studentId) => {
    setStudents((prevStudents) =>
      prevStudents.map((student) =>
        student.studentId === studentId ? { ...student, studentStatus: student.studentStatus === 1 ? 2 : 1 } : student
      )
    );
  };

  const handleOpenEditDialog = (student) => {
    alert(`Edit student not implemented in static version. Student: ${student.studentName}`);
  };

  const handleOpenCreateDialog = () => {
    alert('Create student not implemented in static version');
  };

  // if (isLoading) {
  //   return (
  //     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  return (
    <MainCard>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="All" icon={<Chip label={allCount} color="primary" variant="dark" size="small" />} iconPosition="end" />
          <Tab label="Active" icon={<Chip label={activeCount} color="success" variant="dark" size="small" />} iconPosition="end" />
          <Tab label="Inactive" icon={<Chip label={inactiveCount} color="error" variant="dark" size="small" />} iconPosition="end" />
        </Tabs>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField label="Search Student" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          {/* <Button variant="contained" color="primary" onClick={handleOpenCreateDialog}>
            Create Student
          </Button> */}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 800 }} aria-label="student table">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Student Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Joined Date</TableCell>
              <TableCell>Coach</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStudents.length > 0 ? (
              filteredStudents.map((student, index) => (
                <TableRow key={student.studentId}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    {student.studentName
                      .toLowerCase()
                      .split(' ')
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ')}
                  </TableCell>
                  <TableCell>{student.studentEmail}</TableCell>
                  <TableCell>{student.studentContact}</TableCell>
                  <TableCell>{new Date(student.registrationDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Typography color="primary" fontWeight={600}>
                      {student.coach_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={student.studentStatus === 1 ? 'Active' : 'Inactive'}
                      color={student.studentStatus === 1 ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Tooltip title="View">
                        <IconButton color="secondary" onClick={() => navigate('/students-List/student-details', { state: { student } })}>
                          <BsEyeFill />
                        </IconButton>
                      </Tooltip>
                      {/* Optional future features */}
                      {/* <IconButton color="primary" onClick={() => handleOpenEditDialog(student)}>
              <EditOutlined />
            </IconButton>
            <Switch
              checked={student.studentStatus === 1}
              onChange={() => handleToggleStatus(student.studentId)}
              color="primary"
            /> */}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Lottie options={loaderOptions} height="25%" width="25%" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </MainCard>
  );
}
